import React, { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getAccess } from 'utils';
import DOMPurify from 'dompurify';
import moment from 'moment';
import CreatedInfo from '@/elements/created-info';
import TooltipMenu from '@/elements/TooltipMenu';
import {
  EditIcon,
  ForwardIcon,
  ImportIcon,
  MaximizeIcon,
  TrashIcon,
  CalendarTickIcon,
  LayerIcon,
  LocationIcon,
  DurationIcon,
  BanIcon,
  KeyIcon,
  CategoryIcon,
} from 'assets/icon';
import AnalyticIcon from 'assets/icon/dynamic/AnalyticIcon';
import CardInfo from '@/elements/card-info';
import CardTitle from '@/elements/card-title';
import { Tag } from 'antd';
import { imgUrl } from 'utils/url';
import './style.css';
import defaultLogo from '../../assets/default-logo.png';
import { ReactComponent as PoiIcon } from '../../assets/poi.svg';
import { ReactComponent as PeopleIcon } from '../../assets/people.svg';

const Cards = ({
  data,
  type,
  isShowMedia,
  editAction,
  deleteAction,
  previewAction,
  keyAction,
  downloadAction,
  cardClickAction,
  tagged,
  taggedColor = 'green',
  isPreview = false,
  isDownload = false,
  isClickable = false,
  isEdit = true,
  isDelete = true,
  toAnalytic = false,
}) => {
  const { siteId } = useParams();
  const [access, setAccess] = useState(null);

  const { userData, role } = useSelector(
    (state) => ({
      userData: state.auth.userData,
      role: state.auth.userData.role,
      shapeLookUp: state.editors.shapes,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (userData && userData.roleAccess) {
      setAccess(getAccess(userData, type, siteId));
    }
  }, [userData, type, siteId]);

  const mediaDetails = (
    <div className="card-list-details">
      <CardTitle title={data.name} />
      <CardInfo
        icon={<ForwardIcon style={{ width: 16, height: 16 }} />}
        title={
          data.categoryName &&
          (data.categoryName.length > 20
            ? `${data.categoryName.substr(0, 20)}...`
            : data.categoryName)
        }
      />
      <CardInfo
        icon={<CalendarTickIcon style={{ width: 16, height: 16 }} />}
        title={(() => {
          if (data.schedulingType === 'list') {
            return Array.isArray(data.schedulingIds)
              ? data.schedulingIds.map((schedule) => schedule.name).join(', ')
              : '';
          } else {
            if (data.type === 'date' && data.start && data.end) {
              return `${moment(data.start).format('MMM, D YYYY')} - ${moment(
                data.end
              ).format('MMM, D YYYY')}`;
            }
            if (data.type === 'day' && data.days) {
              return `${data.days[0].day} - ${
                data.days[data.days.length - 1].day
              }`;
            }
            if (data.start && data.end) {
              return `${moment(data.start).format('MMM, D YYYY')} - ${moment(
                data.end
              ).format('MMM, D YYYY')}`;
            }

            return 'All Day';
          }
        })()}
      />
      {data.fileType && (
        <CardInfo
          icon={<DurationIcon style={{ width: 16, height: 16 }} />}
          title={
            data.fileType.split('/')[0] === 'video'
              ? 'Video Duration'
              : data.duration && `${data.duration} seconds`
          }
        />
      )}
    </div>
  );

  const tenantDetails = (
    <div className="card-list-details">
      <CardTitle title={data.name} />

      <CardInfo
        icon={<LayerIcon style={{ width: 16, height: 16 }} />}
        title={(() => {
          // If POI has multiple floors, show all floor names separated by comma
          let floorDisplay = '';
          if (
            data.floorIds &&
            Array.isArray(data.floorIds) &&
            data.floorIds.length > 0
          ) {
            try {
              const floorIds =
                typeof data.floorIds === 'string'
                  ? JSON.parse(data.floorIds)
                  : data.floorIds;
              const floorNames =
                typeof data.floorNames === 'string'
                  ? JSON.parse(data.floorNames)
                  : data.floorNames;
              if (floorNames && floorNames.length > 0) {
                floorDisplay = floorNames.join(', ');
              } else {
                floorDisplay = data.floorName;
              }
            } catch (e) {
              floorDisplay = data.floorName;
            }
          } else {
            floorDisplay = data.floorName;
          }
          return floorDisplay && floorDisplay.length > 20
            ? `${floorDisplay.substr(0, 20)}...`
            : floorDisplay;
        })()}
      />
      <CardInfo
        icon={<LocationIcon style={{ width: 16, height: 16 }} />}
        title={
          data.location && data.location.length > 20
            ? `${data.location.substr(0, 20)}...`
            : data.location
        }
      />
      {tagged && <Tag color={taggedColor}>{tagged}</Tag>}
    </div>
  );

  const roomDetails = (
    <div className="card-list-details">
      <CardTitle title={data.name} />

      <CardInfo
        icon={<PoiIcon />}
        title={
          data.poiIds && data.poiIds.length > 0
            ? data.poiIds.map((poi) => poi.name).join(', ')
            : 'No POI assigned'
        }
      />
      <CardInfo icon={<PeopleIcon />} title={`${data.capacity} People`} />
    </div>
  );

  const HtmlView = ({ htmlString }) => {
    const sanitizedHtmlString = DOMPurify.sanitize(htmlString);
    return (
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: sanitizedHtmlString }}
        style={{ height: '50px', overflow: 'hidden' }}
      />
    );
  };

  const descDetails = (
    <div className="card-list-details">
      <CardTitle title={data.name} />
      {type === 'screen' && role === 'superadmin' && data.apiKeyPreview && (
        <Tag color={'green'}>API Key: {data.apiKeyPreview}</Tag>
      )}
      {data.enableHtmlView ? (
        <HtmlView htmlString={data.description} />
      ) : (
        <p className="card-list-subtitle">{data.description}</p>
      )}
    </div>
  );

  const contentDetails = (
    <div className="card-list-details">
      <CardTitle title={data.title} />
      <CardInfo
        icon={<CategoryIcon style={{ width: 16, height: 16 }} />}
        title={data.category}
      />
      <CardInfo title={data.content} />
    </div>
  );

  const webPagesDetails = (
    <div className="card-list-details">
      <CardTitle title={data.title} />
      <CardInfo title={data.url} />
    </div>
  );

  const dynamicDetails = (
    <div className="card-list-details">
      <CardTitle title={data.name} />
      {data.categoryName && (
        <CardInfo
          icon={<ForwardIcon style={{ width: 16, height: 16 }} />}
          title={
            data.categoryName.length > 20
              ? `${data.categoryName.substr(0, 20)}...`
              : data.categoryName
          }
        />
      )}
      <CardInfo
        icon={<CalendarTickIcon style={{ width: 16, height: 16 }} />}
        title={
          data.start && data.end
            ? `${moment(data.start).format('MMM, D YYYY')} - ${moment(
                data.end
              ).format('MMM, D YYYY')}`
            : 'All Day'
        }
      />
    </div>
  );

  const productDetails = (
    <div className="card-list-details">
      <CardTitle title={data.name} />
      <CardInfo title={data.desc} />
    </div>
  );

  const showByType = () => {
    switch (type) {
      case 'media':
        return mediaDetails;
      case 'dynamic':
        return dynamicDetails;
      case 'tenant':
        return tenantDetails;
      case 'room':
        return roomDetails;
      case 'content':
        return contentDetails;
      case 'web-pages':
        return webPagesDetails;
      case 'web-menu':
        return webPagesDetails;
      case 'product':
        return productDetails;
      default:
        return descDetails;
    }
  };

  const renderMedia = () => {
    if (data.logo && data.fileType) {
      const fileType = data.fileType.split('/')[0];
      if (fileType === 'image') {
        return (
          <img
            src={`${imgUrl}/media/${data.logo}`}
            alt="default"
            className="card-list-img"
            draggable={false}
          />
        );
      }
      if (fileType === 'video') {
        return (
          <video
            id="my-video"
            className="video-js card-list-video"
            data-setup="{}"
            preload="metadata"
          >
            <source src={`${imgUrl}/media/${data.logo}`} type="video/mp4" />
            <track kind="captions" />
          </video>
        );
      }
    }
    return null;
  };

  const renderDynamicMedia = () => {
    if (data.logo && data.fileType) {
      const fileType = data.fileType.split('/')[0];
      if (fileType === 'image') {
        return (
          <img
            src={data.logo}
            alt="default"
            className="card-list-img"
            draggable={false}
          />
        );
      }
      if (fileType === 'video') {
        return (
          <video
            id="my-video"
            className="video-js card-list-video"
            data-setup="{}"
            preload="metadata"
          >
            <source src={data.logo} type="video/mp4" />
            <track kind="captions" />
          </video>
        );
      }
    }
    return null;
  };

  const media = <>{renderMedia()}</>;

  const pois = (
    <img
      src={data.logo ? `${imgUrl}/poi/${data.logo}` : defaultLogo}
      alt="default"
      className="card-list-img"
      draggable={false}
    />
  );

  const roomImage = (
    <img
      src={data.image || defaultLogo}
      alt={data.name}
      className="card-list-img"
      draggable={false}
    />
  );

  return (
    <>
      <div
        className="card-list-container"
        onClick={isClickable ? cardClickAction : () => {}}
      >
        {type === 'tenant' && isShowMedia ? pois : null}
        {type === 'media' && isShowMedia ? media : null}
        {type === 'dynamic' && isShowMedia ? <>{renderDynamicMedia()}</> : null}
        {type === 'room' && isShowMedia ? roomImage : null}
        {showByType()}
      </div>
      <div className="card-list-footer">
        <div className="card-list-footer-left">
          <CreatedInfo user={data.modifiedBy} />
        </div>
        <div
          className="card-list-footer-right"
          onClick={(e) => e.stopPropagation()}
        >
          <ul>
            {isEdit &&
              (role !== 'superadmin' ? (
                (access && access.update) || type === 'room' ? (
                  <li>
                    <TooltipMenu
                      title="Edit"
                      clickAction={editAction}
                      icon={<EditIcon style={{ width: 18, height: 18 }} />}
                    />
                  </li>
                ) : null
              ) : (
                <li>
                  <TooltipMenu
                    title="Edit"
                    clickAction={editAction}
                    icon={<EditIcon style={{ width: 18, height: 18 }} />}
                  />
                </li>
              ))}
            {isPreview && (
              <li>
                <TooltipMenu
                  title="Preview"
                  clickAction={previewAction}
                  icon={<MaximizeIcon style={{ width: 18, height: 18 }} />}
                />
              </li>
            )}
            {type !== 'screen' && isDownload && (
              <li>
                <TooltipMenu
                  title="Download"
                  clickAction={downloadAction}
                  icon={<ImportIcon style={{ width: 18, height: 18 }} />}
                />
              </li>
            )}
            {type === 'screen' && role !== 'superadmin'
              ? null
              : keyAction && (
                  <li>
                    <TooltipMenu
                      title={
                        data.apiKeyPreview
                          ? 'Revoke API Key'
                          : 'Generate API Key'
                      }
                      clickAction={keyAction}
                      icon={
                        data.apiKeyPreview ? (
                          <BanIcon style={{ width: 18, height: 18 }} />
                        ) : (
                          <KeyIcon style={{ width: 18, height: 18 }} />
                        )
                      }
                    />
                  </li>
                )}

            {type !== 'screen' || role !== 'superadmin'
              ? null
              : isDownload && (
                  <li>
                    <TooltipMenu
                      title="Download"
                      clickAction={downloadAction}
                      icon={<ImportIcon style={{ width: 18, height: 18 }} />}
                    />
                  </li>
                )}
            {isDelete &&
              (role !== 'superadmin' ? (
                (access && access.delete) || type === 'room' ? (
                  <li>
                    <TooltipMenu
                      title="Delete"
                      clickAction={deleteAction}
                      icon={<TrashIcon style={{ width: 18, height: 18 }} />}
                    />
                  </li>
                ) : null
              ) : (
                <li>
                  <TooltipMenu
                    title="Delete"
                    clickAction={deleteAction}
                    icon={<TrashIcon style={{ width: 18, height: 18 }} />}
                  />
                </li>
              ))}
            {toAnalytic &&
              (role !== 'superadmin' ? (
                access &&
                access.delete && (
                  <li>
                    <TooltipMenu
                      title="Analytic"
                      clickAction={toAnalytic}
                      icon={<AnalyticIcon style={{ width: 18, height: 18 }} />}
                    />
                  </li>
                )
              ) : (
                <li>
                  <TooltipMenu
                    title="Analytic"
                    clickAction={toAnalytic}
                    icon={<AnalyticIcon style={{ width: 18, height: 18 }} />}
                  />
                </li>
              ))}
          </ul>
        </div>
      </div>
    </>
  );
};

export default Cards;
